import { useTranslation } from 'react-i18next';
import type { SubBrandModule } from 'types/capi/modules';
import type {
  Image as ImageType,
  InternalLink as CapiInternalLink
} from 'types/capi/shared';

import Image, { smallImageOptions } from '~/components/Image';
import Tracked from '~/components/Tracked';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';

function CarouselItem({
  image,
  link,
  modulePosition,
  position
}: {
  image: ImageType;
  link: CapiInternalLink;
  modulePosition: number;
  position: number;
}) {
  const fireNavigationLink = useNavigationLinkInteraction('module');

  return (
    <Tracked.InternalLink
      to={link.path}
      variant="none"
      pagePosition="Homepage Top Silhouettes"
      name="SILHOUETTE_CARD"
      onClick={() =>
        fireNavigationLink(link.path, position, {
          label: link.text,
          modulePosition,
          moduleType: 'homepage-sub-brands'
        })
      }
    >
      <div className="flex flex-col items-center gap-4">
        <div className="flex size-28 items-center justify-center text-clip rounded-full bg-white px-4 py-7 md:size-[160px] md:pb-5 md:pt-3">
          <Image
            src={image.url}
            className="w-full rounded-full"
            alt={image.alt}
            loading="lazy"
            {...smallImageOptions({ pad: true })}
          />
        </div>
        <div className="leading-6">{link.text}</div>
      </div>
    </Tracked.InternalLink>
  );
}

type Props = {
  modulePosition: number;
  subBrands: SubBrandModule['items'];
};

function Carousel({ subBrands, modulePosition }: Props) {
  return (
    <div className="carousel-width no-scrollbar flex items-start gap-2 pb-4 md:justify-between">
      {subBrands.map((sb, i) => (
        <CarouselItem
          image={sb.image}
          link={sb.link}
          key={sb.link.text}
          modulePosition={modulePosition}
          position={i + 1}
        />
      ))}
    </div>
  );
}

export default function SubBrands({ subBrands, modulePosition }: Props) {
  const { t } = useTranslation('home');

  return (
    <div className="w-full bg-concrete">
      <div className="content-width flex flex-col items-center justify-center gap-6 py-10 md:py-20">
        <div className="flex flex-col justify-center gap-6 self-stretch py-0">
          <h2 className="text-title-xl text-center">
            {t('browseMoreTopSilhouettes')}
          </h2>
          <Carousel subBrands={subBrands} modulePosition={modulePosition} />
        </div>
      </div>
    </div>
  );
}
